import { Component, computed, effect, Injector, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { switchMap, takeWhile } from 'rxjs';
import { CalendarPickerComponent } from '../../../../../../../shared/components/forms/calendar-picker/calendar-picker.component';
import { RadioButtonComponent } from '../../../../../../../shared/components/forms/radio-button/radio-button.component';
import { SelectListComponent } from '../../../../../../../shared/components/forms/select-list/select-list.component';
import { TextAreaComponent } from '../../../../../../../shared/components/forms/text-area/text-area.component';
import { templateDrivenFormsViewProviders } from '../../../../../../../shared/directives/form.directive';
import { GAService } from '../../../../services/ga-tags.service';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { getErrorsFromSubmission } from '../../pipes/error-subscription';
import { formValidationGenerator, FormValidators } from '../../pipes/form-validation';

@Component({
  selector: 'app-orbit-qa-flow',
  imports: [
    CalendarPickerComponent,
    FormsModule,
    TextAreaComponent,
    RadioButtonComponent,
    SelectListComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './orbit-qa-flow.component.html',
  styleUrl: './orbit-qa-flow.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
})
export class OrbitQaFlowComponent implements OnInit, OnDestroy {
  formValue = input<Record<string, any>>({});
  formSubmitted = input(false);
  formValid = output<boolean>();

  errors = signal<any>({});

  todaysDate = new Date();

  whenCauseGATagSent = false;

  private _destroyed = false;

  private readonly PARKING_QUESTION = 'safely and legally park';
  private readonly ACCESSIBILITY_QUESTION = 'area around your boiler';
  private readonly WELLBEING_QUESTION = 'health or wellbeing';
  private readonly BOILER_ACCESSIBILITY = 'access your boiler';
  private readonly WATER_SUPPLY_TO_HOME = 'turn off your water supply';

  private readonly PARKING_BLOCKING_MESSAGE =
    "The engineer will have heavy tools and equipment, so it's important that they can park nearby. If parking won't be available please contact the repairer once you have booked your appointment.";
  private readonly ACCESSIBILITY_BLOCKING_MESSAGE =
    'The area around your boiler should be clear so that the engineer can perform your repair safely. If you have any concerns, please contact the repairer once you have booked your appointment.';
  private readonly BOILER_ACCESSIBILITY_BLOCKING_MESSAGE =
    "We won't be able to carry out the repair unless our engineer can safely access your boiler. Please make sure the area around your boiler is clear and easy to reach before your appointment. If you have any concerns, please contact the repairer once you've booked your appointment.";

  private readonly WELLBEING_SUB_QUESTION = { label: 'Please give us some further details', maxLength: 250 };

  private viewModel = computed(() => {
    const latestStep = this.orbitFormClaimService.state.latestStep;

    return {
      isBaxiBooking:
        (this.orbitFormClaimService.state.claimData?.mandatoryData?.bookingOEM ?? '').toLowerCase() === 'baxi',
      faultOptions: this.orbitFormClaimService.state.claimData.mandatoryData.faultData,
      formValue: this.formValue(),
      currentQuestion: latestStep,
      submittingResource: this.orbitFormClaimService.formSubmission,
      listFields:
        latestStep.answerType === 'LIST'
          ? latestStep.answerData.map((data: any) => {
              return {
                title: data.answerLabel,
                value: data,
              };
            })
          : [],
      errors: this.errors(),
      formSubmitted: this.formSubmitted(),
      isCurrentQuestionAboutBoilerAccess: latestStep?.questionLabel?.toLowerCase().includes(this.BOILER_ACCESSIBILITY),
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly injector: Injector,
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly gaService: GAService
  ) {
    effect(() => {
      const { currentQuestion } = this.vm;
      const faultStartQuestions = ['when did the fault happen?', 'when did the fault start?'];
      if (faultStartQuestions.includes(currentQuestion.questionLabel?.toLowerCase()) && !this.whenCauseGATagSent) {
        this.gaService.sendGATag(this.gaService.GA_WHEN_CAUSE.PAGE_LOAD());
        this.whenCauseGATagSent = true;
      }
    });
  }

  get validationType() {
    switch (this.vm.currentQuestion.answerType) {
      case 'DATE':
        return 'Select a date to continue.';
      case 'RESPONSE_FIELD':
        return "Please tell us more or write 'None'.";
      default:
        return 'Select an option to continue.';
    }
  }

  get placeholderTextBreakdown() {
    if (!this.vm.isBaxiBooking) return 'Please add a description';
    if (this.vm.currentQuestion?.questionLabel?.includes('about the problem')) {
      return 'For example, any error messages or changes in pressure.';
    }

    return 'Please add a description';
  }

  get secondaryQuestionPlaceHolderText() {
    if (!this.vm.isBaxiBooking) return undefined;
    if (this.vm.currentQuestion?.questionLabel?.includes('parking available')) {
      return 'Tell us about the parking available near your home.';
    }

    return 'Please add a description';
  }

  get subAnswerValidationType() {
    const selectedValue = this.vm.formValue?.[this.vm.currentQuestion.questionID];
    if (selectedValue?.answerType === 'RESPONSE_FIELD' || selectedValue?.subQuestion || this.effectiveSubQuestion) {
      return 'Provide details before continuing.';
    }
    return 'Select an option to continue.';
  }

  showSubQuestionSection(selectedValue: any): boolean {
    return !!(
      this.effectiveSubQuestion ||
      selectedValue?.answerType === 'DROPDOWN' ||
      selectedValue?.answerType === 'RESPONSE_FIELD'
    );
  }

  get effectiveSubQuestion(): { label: string; maxLength: number } | null {
    const selectedValue = this.vm.formValue?.[this.vm.currentQuestion?.questionID];
    if (selectedValue?.subQuestion) return selectedValue.subQuestion;
    if (
      this.vm.currentQuestion?.questionLabel?.includes(this.WELLBEING_QUESTION) &&
      selectedValue?.answerLabel?.startsWith('Yes')
    ) {
      if (selectedValue?.answerHint) {
        return { label: "We'll use this information to offer the appropriate support.", maxLength: 250 };
      }

      return this.WELLBEING_SUB_QUESTION;
    }

    if (
      this.vm.currentQuestion?.questionLabel?.includes('parking available') &&
      selectedValue?.answerLabel?.startsWith('No')
    ) {
      if (selectedValue?.answerHint) {
        return {
          label: 'For example, let us know if parking is permit-only, pay and display or restricted.',
          maxLength: 250,
        };
      }

      return this.WELLBEING_SUB_QUESTION;
    }
    return null;
  }

  get blockingAnswerMessage(): string | null {
    const selectedValue = this.vm.formValue?.[this.vm.currentQuestion?.questionID];
    if (selectedValue?.blockingMessage) return selectedValue.blockingMessage;
    if (selectedValue?.answerLabel?.startsWith('No')) {
      const questionLabel = this.vm.currentQuestion?.questionLabel;
      if (questionLabel?.includes(this.PARKING_QUESTION)) return this.PARKING_BLOCKING_MESSAGE;
      if (questionLabel?.includes(this.ACCESSIBILITY_QUESTION)) return this.ACCESSIBILITY_BLOCKING_MESSAGE;
      if (questionLabel.includes(this.BOILER_ACCESSIBILITY)) return this.BOILER_ACCESSIBILITY_BLOCKING_MESSAGE;
    }

    if (selectedValue?.answerLabel?.startsWith('Yes')) {
      const questionLabel = this.vm.currentQuestion?.questionLabel;
      if (questionLabel?.includes(this.WATER_SUPPLY_TO_HOME)) return 'WATER_SUPPLY';
    }
    return null;
  }

  get questionHelp() {
    // need to fix when config updated
    switch (this.vm.currentQuestion.answerType) {
      case 'DATE':
        return this.vm.currentQuestion?.answerData[0]?.answerHelp || this.vm.currentQuestion?.questionHelp;
      case 'RESPONSE_FIELD':
        return this.vm.currentQuestion?.questionHelp;
      case 'RADIO_BUTTONS':
        return this.vm.currentQuestion?.questionHelp;
      default:
        return undefined;
    }
  }

  ngOnInit() {
    this.handleFormValidation();
    this.handleAPIErrors();

    // Restore sub-question visibility for resumed sessions (after form bindings settle)
    setTimeout(() => {
      const questionID = this.vm.currentQuestion.questionID;
      const storedAnswer = this.vm.formValue?.[questionID];
      if (storedAnswer?.answerType || storedAnswer?.subQuestion || this.effectiveSubQuestion) {
        this.showSubQuestion.update((s) => ({ ...s, [questionID]: true }));
      }
    });
  }

  ngOnDestroy() {
    this._destroyed = true;
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(this.vm.submittingResource, this.injector, (attempts, error) => {
      console.log({ error });
    });
  }

  handleFormValidation(): void {
    toObservable(this.formValue, { injector: this.injector })
      .pipe(
        takeWhile(() => !this._destroyed),
        switchMap((data) => {
          const currentQuestion = this.vm.currentQuestion;

          let fieldValidation: Record<string, ((value: any) => Record<string, any> | null)[]> = {};
          const answerID = currentQuestion.answerData?.[0]?.answerID;

          const currentAnswer = data?.[currentQuestion.questionID];

          if (currentAnswer?.answerType || currentAnswer?.subQuestion || this.effectiveSubQuestion) {
            fieldValidation[currentQuestion.questionID + '_subAnswer'] = [FormValidators.required];
          }

          if (currentQuestion) {
            switch (currentQuestion.answerType) {
              case 'RESPONSE_FIELD':
              case 'RADIO_BUTTONS':
              case 'LIST':
                fieldValidation[currentQuestion.questionID] = [FormValidators.required];
                break;

              case 'DATE':
                fieldValidation[answerID] = [FormValidators.required];
                break;
            }
          }

          return toObservable(this.formValue, { injector: this.injector }).pipe(
            formValidationGenerator(fieldValidation, [])
          );
        })
      )
      .subscribe({
        next: ({ isFormValid, errors }) => {
          this.formValid.emit(isFormValid);
          this.errors.set(errors);
        },
      });
  }

  // This is needed to reset the sub answer value to ensure there is no invalid data submitted
  answerChangedDelayTimer: ReturnType<typeof setTimeout> | undefined;
  showSubQuestion = signal<Record<string, boolean>>({});
  answerChangedToggle() {
    const questionID = this.vm.currentQuestion.questionID;

    if (this.answerChangedDelayTimer) {
      clearTimeout(this.answerChangedDelayTimer);
    }

    this.showSubQuestion.update((s) => ({ ...s, [questionID]: false }));

    this.answerChangedDelayTimer = setTimeout(() => {
      this.showSubQuestion.update((s) => ({ ...s, [questionID]: true }));
    });
  }
}
